<script setup lang="ts">
import { vMaska } from 'maska/vue';
import type { FormSubmitEvent, FormErrorEvent } from '@nuxt/ui';
import { signupSchema, type SignupFormData } from '@/schemas/signup';
import { mapApiErrors } from '@/utils/mapApiErrors';

export interface SignupFormProps {
  courseType: CourseTypeTitle;
}
const { courseType } = defineProps<SignupFormProps>();

const { getCourses, getIdForCourseType, useSignupMutation } = useHaru();

const course = ref<string>();
const coursesQuery = getCourses();
const signupMutation = useSignupMutation();

const courseTypeId = getIdForCourseType(courseType);

const filteredCourses = computed(() => {
  if (!coursesQuery.data.value?.items.length) return [];

  return coursesQuery.data.value.items
    .filter(c => c.course_type.id === courseTypeId)
    .map((c) => {
      const label = c.meta.availability
        ? `${c.title} (${c.meta.availability})`
        : c.title;

      return { label, value: c.id };
    });
});

const state = reactive<Partial<SignupFormData>>({
  first_name: undefined,
  last_name: undefined,
  email: undefined,
  phone_number: undefined,
  birthday: undefined,
  address: {
    street: '',
    postal_code: '',
    city: '',
  },
  company: undefined,
  vat_number: undefined,
});

const form = ref();
const alertMessage = ref<string>();
const isSuccess = ref(false);

async function onSubmit(event: FormSubmitEvent<SignupFormData>) {
  if (!course.value) return;

  alertMessage.value = undefined;

  try {
    // Convert birthday from DD-MM-YYYY to YYYY-MM-DD
    const [dd, mm, yyyy] = event.data.birthday.split('-');
    const birthday = `${yyyy}-${mm}-${dd}`;

    await signupMutation.mutateAsync({
      body: {
        ...event.data,
        birthday,
      },
      path: {
        course_id: course.value,
      },
    });

    isSuccess.value = true;
  }
  catch (err: unknown) {
    const error = err as { detail?: Array<{ loc: string[]; msg: string; type: string }> | { message?: string } };
    const detail = error?.detail;

    if (detail) {
      if (Array.isArray(detail)) {
        const mapped = mapApiErrors(detail);
        if (Object.keys(mapped).length > 0) {
          form.value?.setErrors(
            Object.entries(mapped).map(([name, message]) => ({ name, message })),
          );
        }
      }
      else {
        alertMessage.value = detail.message;
      }
    }
  }
}

function onError(event: FormErrorEvent) {
  const firstError = event.errors?.[0];
  if (firstError?.id) {
    document.getElementById(firstError.id)?.focus();
  }
}
</script>

<template>
  <div>
    <!-- Loading state -->
<p
        v-if="coursesQuery.isPending.value"
        class="text-muted"
      >
        Indlæser hold…
      </p>

    <!-- No courses state -->
    <template v-else-if="!filteredCourses.length">
      <h3 class="text-lg font-semibold">
        Ingen åbne hold lige nu
      </h3>
      <p class="mt-2">
        Du er altid velkommen til at
        <NuxtLink
          class="text-primary underline"
          :to="{ name: 'kontakt' }"
        >
          kontakte os
        </NuxtLink> — så finder vi sammen en løsning.
      </p>
    </template>

    <!-- Form state -->
    <template v-else>
      <h2 class="text-2xl font-bold mb-2">
        Tilmeld dig her
      </h2>
      <p class="text-sm text-[var(--color-charcoal)]/50 mb-6">
        Vælg et hold og udfyld dine oplysninger — så er du i gang.
      </p>

      <!-- API error alert -->
      <UAlert
        v-if="alertMessage"
        color="error"
        :title="alertMessage"
        class="mb-6"
      />

      <!-- Success state -->
      <div
        v-if="isSuccess"
        class="flex flex-col items-center justify-center gap-4 py-12 text-center"
      >
        <div class="size-16 rounded-full bg-primary-50 flex items-center justify-center">
          <UIcon
            name="i-lucide-circle-check"
            class="text-primary-600 size-10"
          />
        </div>

        <h2 class="text-2xl font-bold">
          Tak for din tilmelding
        </h2>

        <p class="text-sm w-3/4 text-[var(--color-charcoal)]/60 leading-relaxed">
          Du får straks en mail med bekræftelse og praktiske oplysninger om dit hold.
          <br>
          Har du spørgsmål? Du er altid velkommen til at
          <NuxtLink
            class="text-primary-600 font-medium underline underline-offset-2"
            :to="{ name: 'kontakt' }"
          >
            kontakte os
          </NuxtLink>.
        </p>
      </div>

      <!-- Form -->
      <template v-else>
        <UFormField
          label="Vælg et hold"
          class="mb-8"
        >
          <USelect
            v-model="course"
            :items="filteredCourses"
            placeholder="Vælg hold…"
          />
        </UFormField>

        <UForm
          v-if="course"
          ref="form"
          :schema="signupSchema"
          :state="state"
          class="space-y-5"
          @submit="onSubmit"
          @error="onError"
        >
          <!-- Personal info section -->
          <p class="text-xs font-semibold uppercase tracking-wider text-[var(--color-charcoal)]/40 pb-1">
            Om dig
          </p>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <UFormField
              label="Fornavn"
              name="first_name"
              required
            >
              <UInput
                v-model="state.first_name"
                placeholder="Dit fornavn"
                autocomplete="given-name"
              />
            </UFormField>

            <UFormField
              label="Efternavn"
              name="last_name"
              required
            >
              <UInput
                v-model="state.last_name"
                placeholder="Dit efternavn"
                autocomplete="family-name"
              />
            </UFormField>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <UFormField
              label="Email"
              name="email"
              required
            >
              <UInput
                v-model="state.email"
                type="email"
                placeholder="din@email.dk"
                inputmode="email"
                autocomplete="email"
                :spellcheck="false"
              />
            </UFormField>

            <UFormField
              label="Mobil"
              name="phone_number"
              required
            >
              <UInput
                v-model="state.phone_number"
                placeholder="Dit mobilnummer"
                inputmode="tel"
                autocomplete="tel"
              />
            </UFormField>
          </div>

          <UFormField
            label="Fødselsdato"
            name="birthday"
            required
          >
            <UInput
              v-model="state.birthday"
              v-maska="{ mask: '##-##-####', eager: true }"
              placeholder="DD-MM-YYYY"
              autocomplete="bday"
            />
          </UFormField>

          <!-- Address section -->
          <p class="text-xs font-semibold uppercase tracking-wider text-[var(--color-charcoal)]/40 pt-3 pb-1">
            Adresse
          </p>

          <UFormField
            label="Adresse"
            name="address.street"
            required
          >
            <UInput
              v-model="state.address!.street"
              placeholder="Vejnavn og husnummer"
              autocomplete="street-address"
            />
          </UFormField>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <UFormField
              label="Postnummer"
              name="address.postal_code"
              required
            >
              <UInput
                v-model="state.address!.postal_code"
                placeholder="Postnummer"
                autocomplete="postal-code"
              />
            </UFormField>

            <UFormField
              label="By"
              name="address.city"
              required
            >
              <UInput
                v-model="state.address!.city"
                placeholder="By"
                autocomplete="home city"
              />
            </UFormField>
          </div>

          <!-- Company section (optional) -->
          <p class="text-xs font-semibold uppercase tracking-wider text-[var(--color-charcoal)]/40 pt-3 pb-1">
            Virksomhed <span class="font-normal normal-case tracking-normal">(kun ved firmanavn)</span>
          </p>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <UFormField
              label="Firma"
              name="company"
            >
              <UInput
                v-model="state.company"
                placeholder="Evt. firmanavn"
              />
            </UFormField>

            <UFormField
              label="CVR-nummer"
              name="vat_number"
            >
              <UInput
                v-model="state.vat_number"
                placeholder="Evt. CVR-nummer"
              />
            </UFormField>
          </div>

          <div class="pt-4">
            <UButton
              type="submit"
              color="primary"
              :loading="signupMutation.isPending.value"
              block
              size="lg"
              class="rounded-full font-semibold"
            >
              {{ signupMutation.isPending.value ? 'Tilmelder…' : 'Tilmeld nu' }}
            </UButton>
            <p class="text-xs text-center text-[var(--color-charcoal)]/40 mt-3">
              Vi sender en bekræftelse til din email
            </p>
          </div>
        </UForm>
      </template>
    </template>
  </div>
</template>
